import { BookOpenIcon, ChevronDownIcon, ExternalLinkIcon } from "lucide-react";
import type { ComponentProps } from "react";
import { cn } from "../../lib/utils";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "../ui/collapsible";

export type SourcesProps = ComponentProps<typeof Collapsible>;
export const Sources = (props: SourcesProps) => <Collapsible {...props} />;

export type SourcesTriggerProps = ComponentProps<"button"> & { count: number };
export const SourcesTrigger = ({ className, count, children, ...props }: SourcesTriggerProps) => (
	<CollapsibleTrigger asChild>
		<button
			className={cn(
				"group flex items-center gap-1.5 py-1 text-muted-foreground text-xs transition-colors hover:text-foreground",
				className,
			)}
			type="button"
			{...props}
		>
			<BookOpenIcon className="size-3.5" />
			{children ?? `${count} source${count === 1 ? "" : "s"}`}
			<ChevronDownIcon className="size-3.5 transition-transform group-data-[state=open]:rotate-180" />
		</button>
	</CollapsibleTrigger>
);

export type SourcesContentProps = ComponentProps<typeof CollapsibleContent>;
export const SourcesContent = ({ className, ...props }: SourcesContentProps) => (
	<CollapsibleContent
		className={cn("flex flex-col gap-1 border-l pl-3", className)}
		{...props}
	/>
);

export type SourceProps = ComponentProps<"a">;
export const Source = ({ className, children, ...props }: SourceProps) => (
	<a
		className={cn(
			"flex min-w-0 items-center gap-2 rounded-md px-2 py-1.5 text-muted-foreground text-xs transition-colors hover:bg-muted hover:text-foreground",
			className,
		)}
		rel="noreferrer"
		target="_blank"
		{...props}
	>
		<span className="min-w-0 flex-1 truncate">{children}</span>
		<ExternalLinkIcon className="size-3 shrink-0" />
	</a>
);

export type SourceDocumentProps = ComponentProps<"div">;
export const SourceDocument = ({ className, ...props }: SourceDocumentProps) => (
	<div
		className={cn(
			"flex min-w-0 items-center gap-2 rounded-md px-2 py-1.5 text-muted-foreground text-xs",
			className,
		)}
		{...props}
	/>
);
